import type { EditorManifest as SharedEditorManifest } from "../../features/editor/preview-bridge/manifestV3";
import { API_BASE_URL, ApiError, getToken, request, requestSiteMutation } from "./client";
import type {
  EditOp,
  SiteContentDocument,
  SiteContentHead,
  SiteContentResponse,
  SiteVersionContent,
  SiteVersionSummary,
} from "./types";

export const editorApi = {
  /**
   * `canonical` is true when buildId is the site's own build pointer — the id
   * the assistant iterates from and publish promotes. It is false only for the
   * instrumented throwaway copy legacy bundles need, which must never be
   * mistaken for a real build.
   */
  prepareEditor: (siteId: string) =>
    request<{ buildId: string; sourceBuildId?: string; sourceBundleHash?: string; manifest: SharedEditorManifest; canonical: boolean }>(`/api/sites/${siteId}/editor/prepare`, {
      method: "POST",
    }),
  getSiteContent: (siteId: string) =>
    request<SiteContentHead>(`/api/sites/${siteId}/content`),
  /** Chat editor alias for getSiteContent (schema v1 content docs). */
  getContent: (siteId: string) =>
    request<SiteContentResponse>(`/api/sites/${siteId}/content`),
  saveSiteContent: (siteId: string, content: SiteVersionContent, baseVersion: number, label?: string) =>
    requestSiteMutation<{ siteId: string; version: number; unchanged?: boolean }>(`/api/sites/${siteId}/edits`, {
      method: "POST",
      body: JSON.stringify({ content, baseVersion, label }),
    }),
  saveEdit: (
    siteId: string,
    params: { content: SiteContentDocument; baseVersion?: number; label?: string; preview?: boolean },
  ) =>
    requestSiteMutation<{
      siteId: string;
      version: number;
      label: string | null;
      parentVersion: number | null;
      buildId: string | null;
      changes: SiteVersionSummary["changes"];
      createdAt: string;
    }>(`/api/sites/${siteId}/edits`, {
      method: "POST",
      body: JSON.stringify(params),
    }),
  /**
   * Resolve a plain-English edit into field operations without starting a
   * build. `escalated` means it is not a text change — the caller falls back to
   * the edit engine. Never throws the owner's request away: a resolver failure
   * comes back as an escalation, not an error.
   */
  quickEdit: (
    siteId: string,
    body: {
      instruction: string;
      page?: string;
      selectedFieldPath?: string;
      /** set when this message answers a question the resolver asked */
      priorInstruction?: string;
      priorQuestion?: string;
    },
  ) =>
    request<{
      escalated: boolean;
      reason?: string;
      buildId?: string;
      ops?: Array<{ op: "set-field"; path: string; value: string }>;
      summary?: string;
      /** present when it needs one answer before it can act */
      question?: string;
    }>(`/api/sites/${siteId}/editor/quick-edit`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /**
   * `applied: true` means the API already shipped the bundle in-process and
   * there is nothing to poll — the build exists by the time this resolves.
   * Older deployments answer 202 without the flag, so the caller must still
   * handle the polling path.
   */
  applySiteContent: (siteId: string, version: number) =>
    requestSiteMutation<{ jobId: string; buildId?: string; applied?: boolean; elapsedMs?: number }>(
      `/api/sites/${siteId}/builds`,
      { method: "POST", body: JSON.stringify({ kind: "content-apply", version }) },
    ),
  uploadSiteImage: (
    siteId: string,
    file: File,
    options: { signal?: AbortSignal; onProgress?: (percent: number) => void } = {},
  ) => new Promise<{
    assetId: string;
    previewUrl: string;
    previewExpiresAt: string;
    mimeType: string;
    size: number;
    width: number;
    height: number;
    sha256: string;
  }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE_URL}/api/upload/${encodeURIComponent(siteId)}/image`);
    const token = getToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) options.onProgress?.(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.onabort = () => reject(new DOMException("Upload cancelled", "AbortError"));
    xhr.onload = () => {
      let payload: any = {};
      try { payload = JSON.parse(xhr.responseText || "{}"); } catch { /* handled below */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(payload);
      else reject(new ApiError(xhr.status, payload));
    };
    options.signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    const body = new FormData();
    body.append("image", file);
    xhr.send(body);
  }),
  listSiteVersions: (siteId: string) =>
    request<{ siteId: string; currentVersion: number; count: number; versions: SiteVersionSummary[] }>(
      `/api/sites/${siteId}/versions`,
    ),
  listVersions: (siteId: string) =>
    request<{ siteId: string; currentVersion: number; count: number; versions: SiteVersionSummary[] }>(
      `/api/sites/${siteId}/versions`,
    ),
  revertSiteVersion: (siteId: string, version: number, baseVersion: number) =>
    requestSiteMutation<{
      siteId: string;
      version: number;
      revertedFrom: number;
      previewBuildId: string | null;
      liveBuildId: string | null;
      requiresPreviewApply: true;
    }>(`/api/sites/${siteId}/versions/${version}/revert`, {
      method: "POST",
      body: JSON.stringify({ baseVersion }),
    }),
  /** Chat editor revert (optional preview rebuild). */
  revertVersion: (siteId: string, version: number, preview = false) =>
    requestSiteMutation<{
      siteId: string;
      version: number;
      revertedFrom: number;
      parentVersion: number | null;
      buildId: string | null;
      changes: SiteVersionSummary["changes"];
      createdAt: string;
    }>(`/api/sites/${siteId}/versions/${version}/revert`, {
      method: "POST",
      body: JSON.stringify({ preview }),
    }),
  labelSiteVersion: (siteId: string, version: number, label: string | null) =>
    request<{ siteId: string; version: number; label: string | null }>(
      `/api/sites/${siteId}/versions/${version}/label`,
      { method: "PUT", body: JSON.stringify({ label }) },
    ),
  labelVersion: (siteId: string, version: number, label: string | null) =>
    request<{ siteId: string; version: number; label: string | null }>(
      `/api/sites/${siteId}/versions/${version}/label`,
      { method: "PUT", body: JSON.stringify({ label }) },
    ),
  applyOps: (
    siteId: string,
    params: { ops: EditOp[]; baseVersion?: number; label?: string | null; preview?: boolean },
  ) =>
    requestSiteMutation<{
      siteId: string;
      version: number;
      parentVersion: number | null;
      changes: SiteVersionSummary["changes"];
      content: SiteContentDocument;
      buildId: string | null;
      createdAt: string;
    }>(`/api/sites/${siteId}/ops`, {
      method: "POST",
      body: JSON.stringify(params),
    }),
  proposeEditOps: (
    siteId: string,
    prompt: string,
    baseVersion?: number,
    opts?: { section?: string | null; knownPaths?: string[]; activePath?: string | null },
  ) =>
    request<{
      siteId: string;
      baseVersion: number;
      section: string | null;
      activePath?: string | null;
      ops: EditOp[];
      previewContent: SiteContentDocument;
      summary: string;
    }>(`/api/sites/${siteId}/ai-edit-proposals`, {
      method: "POST",
      body: JSON.stringify({
        prompt,
        baseVersion,
        section: opts?.section,
        knownPaths: opts?.knownPaths,
        activePath: opts?.activePath,
      }),
    }),
};
